/* eslint-disable */
import React from 'react';
import { Button, CircularProgress, Box } from '@mui/material';
import AddOutlinedIcon           from '@mui/icons-material/AddOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CloseOutlinedIcon         from '@mui/icons-material/CloseOutlined';
import VisibilityOutlinedIcon    from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon          from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon          from '@mui/icons-material/SaveOutlined';
import FileDownloadOutlinedIcon  from '@mui/icons-material/FileDownloadOutlined';
import SearchOutlinedIcon        from '@mui/icons-material/SearchOutlined';
import FilterAltOffIcon          from '@mui/icons-material/FilterAltOff';
import CheckOutlinedIcon         from '@mui/icons-material/CheckOutlined';
import SendOutlinedIcon          from '@mui/icons-material/SendOutlined';
import RefreshOutlinedIcon       from '@mui/icons-material/RefreshOutlined';
import PrintOutlinedIcon         from '@mui/icons-material/PrintOutlined';
import ArrowBackOutlinedIcon     from '@mui/icons-material/ArrowBackOutlined';
import UploadOutlinedIcon        from '@mui/icons-material/UploadOutlined';

/**
 * ─────────────────────────────────────────────────────────────
 *  AppButton — one reusable button for every situation.
 *
 *  Use the `variant` shortcut to get a pre-configured button,
 *  or compose your own with individual props.
 * ─────────────────────────────────────────────────────────────
 *
 *  PRESET VARIANTS  (variant prop)
 *  ─────────────────
 *  "add"         → blue  + AddIcon         "Ajouter"
 *  "delete"      → red   + DeleteIcon      "Supprimer"
 *  "cancel"      → grey  + CloseIcon       "Annuler"
 *  "consult"     → blue outline + EyeIcon  "Consulter"
 *  "edit"        → blue  + EditIcon        "Modifier"
 *  "save"        → green + SaveIcon        "Enregistrer"
 *  "download"    → blue outline + DlIcon   "Télécharger"
 *  "search"      → blue  + SearchIcon      "Rechercher"
 *  "reset"       → grey  + FilterOffIcon   "Réinitialiser"
 *  "validate"    → green + CheckIcon       "Valider"
 *  "send"        → blue  + SendIcon        "Envoyer"
 *  "refresh"     → grey  + RefreshIcon     "Actualiser"
 *  "print"       → grey outline + PrintIcon"Imprimer"
 *  "back"        → grey  + BackIcon        "Retour"
 *  "upload"      → blue outline + UploadIcon "Importer"
 *  "custom"      → no defaults — set everything yourself
 *
 *  All presets can be overridden with any prop below.
 * ─────────────────────────────────────────────────────────────
 *
 *  Props:
 *  variant      {string}    — preset name (see list above) or MUI variant
 *  label        {string}    — override the preset label
 *  color        {string}    — hex color (overrides preset color)
 *  hoverColor   {string}    — hover background hex
 *  textColor    {string}    — label color (for outlined/ghost)
 *  size         {string}    — "small" | "medium" | "large" (default "medium")
 *  icon         {ReactNode} — override the preset icon
 *  iconPosition {string}    — "start" | "end" (default "start")
 *  loading      {boolean}   — show spinner, disable click
 *  disabled     {boolean}
 *  fullWidth    {boolean}
 *  onClick      {function}
 *  type         {string}    — "button" | "submit" | "reset"
 *  sx           {object}    — extra MUI sx (merged on top)
 * ─────────────────────────────────────────────────────────────
 */

/* ── Preset definitions ──────────────────────────────────── */
const PRESETS = {
  add: {
    label: 'Ajouter',
    icon: <AddOutlinedIcon />,
    bg: '#007BC6',
    hover: '#005F9E',
    text: '#fff',
    muiVariant: 'contained',
  },
  delete: {
    label: 'Supprimer',
    icon: <DeleteOutlineOutlinedIcon />,
    bg: '#DC2626',
    hover: '#B91C1C',
    text: '#fff',
    muiVariant: 'contained',
  },
  cancel: {
    label: 'Annuler',
    icon: <CloseOutlinedIcon />,
    bg: '#F1F5F9',
    hover: '#E2E8F0',
    text: '#64748B',
    muiVariant: 'contained',
  },
  consult: {
    label: 'Consulter',
    icon: <VisibilityOutlinedIcon />,
    bg: 'transparent',
    hover: '#EBF5FB',
    text: '#007BC6',
    border: '#007BC6',
    muiVariant: 'outlined',
  },
  edit: {
    label: 'Modifier',
    icon: <EditOutlinedIcon />,
    bg: '#007BC6',
    hover: '#005F9E',
    text: '#fff',
    muiVariant: 'contained',
  },
  save: {
    label: 'Enregistrer',
    icon: <SaveOutlinedIcon />,
    bg: '#16A34A',
    hover: '#15803D',
    text: '#fff',
    muiVariant: 'contained',
  },
  download: {
    label: 'Télécharger',
    icon: <FileDownloadOutlinedIcon />,
    bg: 'transparent',
    hover: '#EBF5FB',
    text: '#007BC6',
    border: '#007BC6',
    muiVariant: 'outlined',
  },
  search: {
    label: 'Rechercher',
    icon: <SearchOutlinedIcon />,
    bg: '#007BC6',
    hover: '#005F9E',
    text: '#fff',
    muiVariant: 'contained',
  },
  reset: {
    label: 'Réinitialiser',
    
    bg: '#007BC6',
    hover: '#005F9E',
    text: '#fff',
    muiVariant: 'contained',
  },
  validate: {
    label: 'Valider',
    icon: <CheckOutlinedIcon />,
    bg: '#16A34A',
    hover: '#15803D',
    text: '#fff',
    muiVariant: 'contained',
  },
  send: {
    label: 'Envoyer',
    icon: <SendOutlinedIcon />,
    bg: '#007BC6',
    hover: '#005F9E',
    text: '#fff',
    muiVariant: 'contained',
  },
  refresh: {
    label: 'Actualiser',
    icon: <RefreshOutlinedIcon />,
    bg: '#F1F5F9',
    hover: '#E2E8F0',
    text: '#64748B',
    muiVariant: 'contained',
  },
  print: {
    label: 'Imprimer',
    icon: <PrintOutlinedIcon />,
    bg: 'transparent',
    hover: '#F1F5F9',
    text: '#64748B',
    border: '#CBD5E0',
    muiVariant: 'outlined',
  },
  back: {
    label: 'Retour',
    icon: <ArrowBackOutlinedIcon />,
    bg: '#F1F5F9',
    hover: '#E2E8F0',
    text: '#64748B',
    muiVariant: 'contained',
  },
  upload: {
    label: 'Importer',
    icon: <UploadOutlinedIcon />,
    bg: 'transparent',
    hover: '#EBF5FB',
    text: '#007BC6',
    border: '#007BC6',
    muiVariant: 'outlined',
  },
  custom: {
    label: '',
    icon: null,
    bg: '#007BC6',
    hover: '#005F9E',
    text: '#fff',
    muiVariant: 'contained',
  },
};

/* ── Size map ─────────────────────────────────────────────── */
const SIZE_MAP = {
  small:  { height: '34px', fontSize: '12px', px: '14px', iconSize: 15 },
  medium: { height: '42px', fontSize: '13px', px: '18px', iconSize: 17 },
  large:  { height: '50px', fontSize: '14px', px: '24px', iconSize: 19 },
};

/* ── AppButton ───────────────────────────────────────────── */
const AppButton = ({
  variant = 'custom',
  label,
  color,
  hoverColor,
  textColor,
  size = 'medium',
  icon,
  iconPosition = 'start',
  loading = false,
  disabled = false,
  fullWidth = false,
  onClick,
  type = 'button',
  sx = {},
}) => {
  const preset    = PRESETS[variant] || PRESETS.custom;
  const sizes     = SIZE_MAP[size]   || SIZE_MAP.medium;

  const bg        = color      || preset.bg;
  const hover     = hoverColor || preset.hover;
  const txt       = textColor  || preset.text;
  const border    = preset.border;
  const muiVar    = preset.muiVariant;
  const finalLabel = label !== undefined ? label : preset.label;
  const finalIcon  = icon  !== undefined ? icon  : preset.icon;

  /* Clone icon to inject the right size */
  const iconEl = finalIcon && React.isValidElement(finalIcon)
    ? React.cloneElement(finalIcon, {
        sx: { fontSize: sizes.iconSize, ...finalIcon.props?.sx },
      })
    : finalIcon;

  /* Build sx for outlined variant */
  const outlinedOverride = muiVar === 'outlined' ? {
    borderColor: border || bg,
    '&:hover': { borderColor: hover || bg, backgroundColor: hover },
  } : {};

  return (
    <Button
      type={type}
      variant={muiVar}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      onClick={onClick}
      startIcon={!loading && iconPosition === 'start' ? iconEl : undefined}
      endIcon={!loading && iconPosition === 'end' ? iconEl : undefined}
      sx={{
        height: sizes.height,
        px: sizes.px,
        fontSize: sizes.fontSize,
        fontWeight: 600,
        textTransform: 'none',
        borderRadius: '8px',
        boxShadow: 'none',
        flexShrink: 0,
        backgroundColor: muiVar === 'outlined' ? bg : bg,
        color: txt,
        '&:hover': {
          backgroundColor: hover,
          boxShadow: 'none',
        },
        '&.Mui-disabled': {
          backgroundColor: muiVar === 'outlined' ? 'transparent' : '#E2E8F0',
          color: '#A0AEC0',
          borderColor: '#E2E8F0',
        },
        ...outlinedOverride,
        ...sx,
      }}
    >
      {loading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CircularProgress size={sizes.iconSize} sx={{ color: txt }} />
          {finalLabel}
        </Box>
      ) : finalLabel}
    </Button>
  );
};

export default AppButton;

/*
 ═══════════════════════════════════════════════════════════════
  USAGE EXAMPLES
 ═══════════════════════════════════════════════════════════════

 import AppButton from 'Shared/UI/Button';

 ── Preset variants ───────────────────────────────────────────
 <AppButton variant="add"      onClick={handleAdd}    />
 <AppButton variant="delete"   onClick={handleDelete} />
 <AppButton variant="cancel"   onClick={handleCancel} />
 <AppButton variant="consult"  onClick={handleView}   />
 <AppButton variant="edit"     onClick={handleEdit}   />
 <AppButton variant="save"     onClick={handleSave}   />
 <AppButton variant="validate" onClick={handleSubmit} />
 <AppButton variant="download" onClick={handleDl}     />
 <AppButton variant="reset"    onClick={handleReset}  />
 <AppButton variant="back"     onClick={() => history.goBack()} />

 ── Override label only ───────────────────────────────────────
 <AppButton variant="add"    label="Nouvelle annonce" onClick={...} />
 <AppButton variant="delete" label="Retirer l'accès"  onClick={...} />
 <AppButton variant="save"   label="Confirmer"        onClick={...} />

 ── Override color ────────────────────────────────────────────
 <AppButton variant="add" color="#9333EA" hoverColor="#7E22CE" label="Créer" />

 ── Override size ─────────────────────────────────────────────
 <AppButton variant="add"    size="small"  onClick={...} />
 <AppButton variant="delete" size="large"  onClick={...} />

 ── Loading state ─────────────────────────────────────────────
 <AppButton variant="save" loading={isSaving} onClick={handleSave} />

 ── Icon on right ─────────────────────────────────────────────
 <AppButton variant="download" iconPosition="end" onClick={...} />

 ── Fully custom ──────────────────────────────────────────────
 <AppButton
   variant="custom"
   label="Exporter CSV"
   color="#0F766E"
   hoverColor="#0D9488"
   icon={<IosShareIcon />}
   size="medium"
   onClick={handleExport}
 />

 ── In a form footer ──────────────────────────────────────────
 <Box sx={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
   <AppButton variant="cancel"   onClick={onClose}  />
   <AppButton variant="validate" onClick={onSubmit} loading={saving} />
 </Box>
*/
